import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ChevronDown, ChevronRight, Users, UserX, TrendingUp, DoorOpen, Pencil, Check, X, Download, Sparkles, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { kursbestandAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import KursbestandVorschlaege from '@/components/KursbestandVorschlaege';

/* Kursbestand (18.09.2026): welches Mitglied trainiert in welchem Kurs.
   Drei Sichten: Kurse (Stammkunden, freie Plaetze), Ohne Kurs (zuordnen),
   Upsells (Hinweise je Mitglied). Zuordnung direkt in der Zeile. */

const P1_ROT = 'hsl(var(--marke))';   // im CRM die gewählte Akzentfarbe (Neugestaltung 12.10.2026)
const TAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const HINWEIS_CLS = {
  tarif_zu_klein: 'bg-red-500/10 text-red-700 border-red-200', zweite_einheit: 'bg-amber-500/10 text-amber-700 border-amber-200',
  gym_sauna: 'bg-sky-500/10 text-sky-700 border-sky-200', family: 'bg-violet-500/10 text-violet-700 border-violet-200',
  alttarif: 'bg-orange-500/10 text-orange-700 border-orange-200', verlaengerung: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
  abgelaufen: 'bg-red-500/10 text-red-700 border-red-200',
};
const HINWEIS_KURZ = { tarif_zu_klein: 'Tarif zu klein', zweite_einheit: '2. Einheit', gym_sauna: 'Gym & Sauna', family: 'Family', alttarif: 'Alt-Tarif', verlaengerung: 'Verlängerung', abgelaufen: 'Abgelaufen' };

function einheitenText(n) { return n >= 99 ? '∞' : n; }

function Hinweise({ liste, kurz }) {
  if (!liste?.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {liste.map(h => <Badge key={h.art} variant="outline" className={`text-[10px] ${HINWEIS_CLS[h.art] || ''}`} title={h.text}>{kurz ? HINWEIS_KURZ[h.art] || h.art : h.text}</Badge>)}
    </span>
  );
}

/* Zuordnung: Kurse als Kästchen nach Wochentag, speichert per Haken. */
function Zuordnen({ mitglied, kurse, onGespeichert, onAbbruch }) {
  const [gewaehlt, setGewaehlt] = useState(new Set(mitglied.stammkurse || []));
  const [speichert, setSpeichert] = useState(false);
  const toggle = (id) => setGewaehlt(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const speichern = async () => {
    setSpeichert(true);
    try { await kursbestandAPI.zuordnen(mitglied.id, [...gewaehlt]); toast.success(`${mitglied.name}: ${gewaehlt.size} Kurs${gewaehlt.size === 1 ? '' : 'e'} gespeichert`); onGespeichert(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSpeichert(false); }
  };
  return (
    <div className="mt-2 rounded-md border border-border bg-muted/20 p-3 space-y-2" data-testid={`zuordnen-${mitglied.id}`}>
      {mitglied.gruppe && !gewaehlt.size && <p className="text-xs text-muted-foreground">Aus dem Tarif: <b>{mitglied.gruppe}</b> — bitte den Wochentag ankreuzen.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1">
        {TAGE.map(tag => {
          const desTages = kurse.filter(k => k.day === tag);
          if (!desTages.length) return null;
          return (
            <div key={tag} className="text-xs">
              <p className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px] mt-1">{tag}</p>
              {desTages.map(k => (
                <label key={k.id} className={`flex items-center gap-2 py-0.5 cursor-pointer ${mitglied.gruppe && k.name === mitglied.gruppe ? 'font-semibold' : ''}`}>
                  <input type="checkbox" className="accent-marke" checked={gewaehlt.has(k.id)} onChange={() => toggle(k.id)} />
                  <span className="tabular-nums text-muted-foreground w-10">{k.start}</span>
                  <span>{k.name}</span>
                  <span className="text-muted-foreground ml-auto">{k.belegt}/{k.max}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs gap-1" onClick={speichern} disabled={speichert}>{speichert ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Speichern</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={onAbbruch}><X className="w-3 h-3" /> Abbrechen</Button>
      </div>
    </div>
  );
}

function MitgliedZeile({ m, kurse, darfZuordnen, onGespeichert, navigate, zeigeKurse }) {
  const [offen, setOffen] = useState(false);
  const kursNamen = (m.stammkurse || []).map(id => kurse.find(k => k.id === id)).filter(Boolean);
  return (
    <li className="py-2 border-t border-border/50 first:border-t-0 text-sm">
      <div className="flex items-start gap-2 flex-wrap">
        <button className="font-medium hover:underline text-left" onClick={() => navigate(`/leads/${m.id}${m.hinweise?.length ? '?upsell=1' : ''}`)} title={m.hinweise?.length ? 'Lead-Karte mit Upsell-Dialog öffnen' : 'Lead-Karte öffnen'}>{m.name}</button>
        {m.kind && <Badge variant="secondary" className="text-[10px]">Kind</Badge>}
        <span className="text-xs text-muted-foreground">{m.tarif} · {einheitenText(m.einheiten)}× / Woche{m.einheiten_sicher ? '' : ' (angenommen)'}{m.besuche_8w ? ` · ${m.besuche_8w} Besuche in 8 Wo.` : ''}</span>
        <span className="ml-auto flex items-center gap-2">
          <Hinweise liste={m.hinweise} kurz />
          {darfZuordnen && !offen && <button className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent" title="Kurse zuordnen" onClick={() => setOffen(true)} data-testid={`zuordnen-btn-${m.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
        </span>
      </div>
      {zeigeKurse && (
        <p className="text-xs text-muted-foreground mt-0.5">{kursNamen.length ? kursNamen.map(k => `${k.day.slice(0, 2)} ${k.start} ${k.name}`).join(' · ') : m.gruppe ? <span><b>{m.gruppe}</b> laut Tarif — Tag noch zuordnen</span> : 'kein Kurs zugeordnet'}</p>
      )}
      {offen && <Zuordnen mitglied={m} kurse={kurse} onGespeichert={() => { setOffen(false); onGespeichert(); }} onAbbruch={() => setOffen(false)} />}
    </li>
  );
}

function KursKarte({ k, kurse, darfZuordnen, onGespeichert, navigate }) {
  const [offen, setOffen] = useState(false);
  const voll = k.frei === 0;
  return (
    <Card className="border-border/60" data-testid={`kurs-${k.id}`}>
      <button type="button" className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap" onClick={() => setOffen(o => !o)} aria-expanded={offen}>
        {offen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <span className="tabular-nums text-sm text-muted-foreground w-20">{k.day.slice(0, 2)} {k.start}</span>
        <span className="font-semibold text-sm">{k.name}</span>
        {k.note && <span className="text-xs text-muted-foreground">{k.note}</span>}
        {k.trainer && <span className="text-xs text-muted-foreground">· {k.trainer}</span>}
        {k.assistenz && <span className="text-xs text-muted-foreground">· Assistenz: {k.assistenz}</span>}
        <span className="ml-auto flex items-center gap-2">
          <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block"><div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round((k.belegt / (k.max || 1)) * 100))}%`, backgroundColor: voll ? '#EF4444' : P1_ROT, opacity: 0.8 }} /></div>
          <span className="tabular-nums text-sm">{k.belegt}/{k.max}</span>
          <Badge variant="outline" className={`text-[10px] ${k.frei >= 4 ? 'border-emerald-300 text-emerald-600' : voll ? 'border-red-300 text-red-600' : 'border-amber-300 text-amber-600'}`}>{voll ? 'voll' : `${k.frei} frei`}</Badge>
          {k.upsell_kandidaten > 0 && <Badge variant="secondary" className="text-[10px]">{k.upsell_kandidaten} Upsell</Badge>}
          {k.gruppe_ohne_tag > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600" title={`${k.gruppe_ohne_tag} Mitglieder der Gruppe ${k.name} haben noch keinen Tag`}>{k.gruppe_ohne_tag} ohne Tag</Badge>}
        </span>
      </button>
      {offen && (
        <CardContent className="px-4 pb-3 pt-0">
          {k.mitglieder.length === 0 ? <p className="text-xs text-muted-foreground py-2">Noch niemand zugeordnet — über „Ohne Kurs" oder den Stift am Mitglied zuordnen.</p>
            : <ul>{k.mitglieder.map(m => <MitgliedZeile key={m.id} m={{ ...m, stammkurse: undefined }} kurse={kurse} darfZuordnen={false} onGespeichert={onGespeichert} navigate={navigate} />)}</ul>}
          {k.trial_slots > 0 && <p className="text-[10px] text-muted-foreground mt-1">{k.trial_slots} Plätze für Probetrainings reserviert.</p>}
        </CardContent>
      )}
    </Card>
  );
}

export default function KursbestandPage() {
  const navigate = useNavigate();
  const { user, isAdminPermissions, userPermissions } = useAuth();
  const darfZuordnen = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions || !!(userPermissions || {})['leads.edit'];
  const istAdmin = user?.role === 'admin' || user?.role === 'manager' || isAdminPermissions;
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(true);
  const [sicht, setSicht] = useState('kurse');
  const [tag, setTag] = useState('');
  const [suche, setSuche] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    try { setDaten((await kursbestandAPI.lesen()).data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Kursbestand konnte nicht geladen werden'); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { lade(); }, [lade]);

  const vorschlag = async () => {
    try { const r = await kursbestandAPI.erstvorschlag(); toast.success(`${r.data.gesetzt} Mitglieder einer Gruppe zugeordnet — Tage bitte nachtragen`); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Vorschlag fehlgeschlagen'); }
  };
  const exportCsv = () => {
    const zeilen = [['Name', 'Telefon', 'Tarif', 'Einheiten', 'Kurse', 'Hinweise']];
    (daten?.upsells || []).forEach(m => zeilen.push([m.name, m.phone || '', m.tarif || '', einheitenText(m.einheiten),
      (m.stammkurse || []).map(id => daten.kurse.find(k => k.id === id)?.name).filter(Boolean).join(' | '), m.hinweise.map(h => h.text).join(' | ')]));
    const csv = zeilen.map(z => z.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })); a.download = 'upsell-kandidaten.csv'; a.click();
  };

  const kurseGefiltert = useMemo(() => (daten?.kurse || []).filter(k => !tag || k.day === tag), [daten, tag]);
  const s = suche.trim().toLowerCase();
  const passt = (m) => !s || (m.name || '').toLowerCase().includes(s) || (m.tarif || '').toLowerCase().includes(s);
  const z = daten?.zusammenfassung;

  if (laden && !daten) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="kursbestand-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Users className="w-6 h-6" style={{ color: P1_ROT }} /> Kursbestand</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Wer trainiert in welchem Kurs — für sichere Upsells und Neuverkäufe. Stammkurse je Mitglied pflegbar.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {istAdmin && <Button size="sm" variant="outline" onClick={vorschlag} className="gap-1.5" title="Kids Class, Jugend, Minis, Ladies Boxing, Wettkampf aus dem Tarifnamen"><Sparkles className="w-4 h-4" /> Vorschlag aus Tarif</Button>}
          <Button size="sm" variant="outline" onClick={lade} disabled={laden} className="gap-1.5">{laden ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</Button>
        </div>
      </div>

      {z && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[[Users, 'Mitglieder', z.mitglieder, `${z.zugeordnet} zugeordnet`, P1_ROT], [UserX, 'Ohne Tag / Kurs', z.ohne_kurs, `${z.ohne_tag} mit Gruppe aus dem Tarif`, '#F59E0B'],
            [TrendingUp, 'Upsell-Kandidaten', z.upsell_kandidaten, `${z.upsell_angebote} mit Angebot · ${z.alttarife} Alt-Tarife`, '#10B981'], [DoorOpen, 'Freie Plätze', z.freie_plaetze, `${z.kurse_mit_platz} Kurse mit 4+ frei`, '#3B82F6'],
            [Sparkles, 'Kurse', daten.kurse.length, 'im Kursplan', '#8B5CF6']].map(([Icon, l, w, zusatz, farbe]) => (
            <Card key={l} className="border-border/60"><CardContent className="p-3 sm:p-4 flex items-start justify-between gap-2">
              <div><p className="text-[10px] sm:text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{l}</p><p className="text-xl sm:text-2xl font-bold tabular-nums">{w}</p><p className="text-[10px] text-muted-foreground">{zusatz}</p></div>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `color-mix(in srgb, ${farbe} 12%, transparent)` }}><Icon className="w-4 h-4" style={{ color: farbe }} /></div>
            </CardContent></Card>
          ))}
        </div>
      )}

      {/* lernt aus den Check-ins, wer wo Stammkunde ist (13.10.2026) */}
      <KursbestandVorschlaege leitung={istAdmin} onGeaendert={lade} />

      <div className="flex items-center gap-2 flex-wrap">
        {[['kurse', 'Kurse'], ['ohne', `Ohne Kurs (${z?.ohne_kurs ?? 0})`], ['upsells', `Upsells (${z?.upsell_angebote ?? 0})`], ['alle', 'Alle Mitglieder']].map(([k, l]) => (
          <Button key={k} size="sm" variant={sicht === k ? 'default' : 'outline'} onClick={() => setSicht(k)} data-testid={`sicht-${k}`}>{l}</Button>
        ))}
        {sicht === 'kurse' && (
          <select value={tag} onChange={e => setTag(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm ml-auto">
            <option value="">Alle Tage</option>{TAGE.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {sicht !== 'kurse' && <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Name oder Tarif suchen" className="h-9 w-56 ml-auto" />}
        {sicht === 'upsells' && <Button size="sm" variant="outline" className="gap-1.5" onClick={exportCsv}><Download className="w-4 h-4" /> CSV</Button>}
      </div>

      {sicht === 'kurse' && !tag && daten.gruppen?.length > 0 && (
        <Card className="border-border/60"><CardContent className="p-4">
          <h2 className="text-sm font-semibold mb-2">Gruppen: Kapazität über alle Tage</h2>
          {/* Alle Kurse, auch leere (Betreiber 23.09.2026) — belegte zuerst, dann nach Name */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 text-xs">
            {[...daten.gruppen].sort((a, b) => ((b.mit_tag + b.ohne_tag) > 0) - ((a.mit_tag + a.ohne_tag) > 0) || a.name.localeCompare(b.name, 'de')).map(g => (
              <div key={g.name} className="flex items-center gap-2 py-1 border-b border-border/40">
                <span className={`font-medium flex-1 ${g.mit_tag + g.ohne_tag === 0 ? 'text-muted-foreground' : ''}`}>{g.name} <span className="text-muted-foreground font-normal">· {g.tage.length} Tag{g.tage.length === 1 ? '' : 'e'}</span></span>
                <span className="tabular-nums">{g.mit_tag + g.ohne_tag} / {g.kapazitaet}</span>
                {g.ohne_tag > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">{g.ohne_tag} ohne Tag</Badge>}
                <Badge variant="outline" className={`text-[10px] ${g.frei >= 4 ? 'border-emerald-300 text-emerald-600' : 'border-red-300 text-red-600'}`}>{g.frei} frei</Badge>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
      {sicht === 'kurse' && (
        <div className="space-y-2">
          {kurseGefiltert.map(k => <KursKarte key={k.id} k={k} kurse={daten.kurse} darfZuordnen={darfZuordnen} onGespeichert={lade} navigate={navigate} />)}
        </div>
      )}
      {sicht !== 'kurse' && (
        <Card className="border-border/60"><CardContent className="px-4 py-2">
          {(() => {
            const liste = (sicht === 'ohne' ? daten.ohne_kurs : sicht === 'upsells' ? daten.upsells : daten.mitglieder).filter(passt);
            if (!liste.length) return <p className="text-sm text-muted-foreground py-6 text-center">{sicht === 'ohne' ? 'Alle Mitglieder sind einem Kurs zugeordnet.' : 'Keine Einträge.'}</p>;
            return <ul>{liste.map(m => <MitgliedZeile key={m.id} m={m} kurse={daten.kurse} darfZuordnen={darfZuordnen} onGespeichert={lade} navigate={navigate} zeigeKurse />)}</ul>;
          })()}
        </CardContent></Card>
      )}
      <p className="text-[11px] text-muted-foreground max-w-3xl">Der Tarifname verrät nur die Gruppe (Kids Class 1, Jugend, Minis …), nicht den Wochentag — den trägt das Team über den Stift nach. Einheiten je Woche kommen aus dem Tarifnamen; bei Kinderplänen ohne Angabe wird 1× angenommen. Besuche zählen Check-ins der letzten 8 Wochen. Hinweise: Tarif zu klein, 2. Einheit, Gym &amp; Sauna, Family, Alt-Tarif, Verlängerung.</p>
    </div>
  );
}
